import { useCreateSourceMaterial, useUpdateSourceMaterial } from 'hooks/useSourceMaterials'
import Styles from 'screens/BrandKits/components/SourceMaterialModal/SourceMaterialModal.module.scss'
import { Modal, useModals } from 'almanac-ui/Modal'
import { HighDetailedSource } from 'blaze-ui/icons/24'
import { Heading } from 'blaze-ui/Heading/Heading'
import { EmojiPicker } from 'components/AlmanacFileTitle/EmojiPicker'
import React, { ReactElement, useState } from 'react'
import { Input } from 'almanac-ui/TextInputField/Input'
import {
  CreateKnowledgeBaseSourceMaterialPayload,
  UpdateKnowledgeBaseSourceMaterialPayload,
} from 'api/clients/KnowledgeBaseClient'
import ReactMarkdown from 'react-markdown'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from './MobileHeader'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ShowTranscriptionButton } from 'screens/BrandKits/components/SourceMaterialModal/ShowTranscriptionButton'
import { KnowledgeBaseSourceMaterial } from 'types'

export type SourceMaterial = CreateKnowledgeBaseSourceMaterialPayload | UpdateKnowledgeBaseSourceMaterialPayload

export interface Props {
  onHandleSourceMaterialSuccess: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  sourceMaterial: SourceMaterial
}

interface PreviewProps {
  sourceMaterial: SourceMaterial
}

const Preview = ({ sourceMaterial }: PreviewProps) => {
  if (sourceMaterial.screenshot?.length) {
    return (
      <div className={Styles.preview}>
        <div className={Styles.transcriptionButton}>
          <ShowTranscriptionButton content={sourceMaterial.content} />
        </div>
        <img
          src={sourceMaterial.screenshot}
          alt={sourceMaterial.source_url || sourceMaterial.name}
          className={Styles.screenshot}
        />
      </div>
    )
  }

  return (
    <div className={Styles.preview}>
      <div className={Styles.transcriptionButton}>
        <ShowTranscriptionButton content={sourceMaterial.content} />
      </div>
      <ReactMarkdown className={Styles.content}>{sourceMaterial.content}</ReactMarkdown>
    </div>
  )
}

export const SourcePreviewModal = ({ sourceMaterial: initialSourceMaterial, onHandleSourceMaterialSuccess }: Props) => {
  const { closeModals, closeAllModals, closeModal } = useModals()
  const [sourceMaterial, setSourceMaterial] = useState<SourceMaterial>({
    ...initialSourceMaterial,
  })
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [showError, setShowError] = useState(false)
  const { mutateAsync: createSourceMaterial, isLoading: isCreating } = useCreateSourceMaterial()
  const { mutateAsync: updateSourceMaterial, isLoading: isUpdating } = useUpdateSourceMaterial()
  const { isMobile } = useResponsiveDesign()

  const handleUpsert = async () => {
    try {
      const result =
        'id' in sourceMaterial ? await updateSourceMaterial(sourceMaterial) : await createSourceMaterial(sourceMaterial)

      closeModal()
      onHandleSourceMaterialSuccess(result)
    } catch (err) {
      if (err instanceof Error) {
        setNameError(err.message)
      } else {
        setNameError('An unknown error occurred')
      }
    }
  }

  const isValid = sourceMaterial.name?.length && !nameError

  const handleSubmit = () => {
    if (!isValid) {
      setShowError(true)
      return
    }

    handleUpsert()
  }

  const handleBack = () => {
    closeModals(2)
  }

  return (
    <Modal.Root
      size="sm"
      blaze
      onClose={handleBack}
      onPressOutside={closeAllModals}
      aria-label="Preview Source Material"
    >
      <ResponsiveDesign.Desktop>
        <Modal.Header
          title={
            <div className={Styles.header}>
              <HighDetailedSource size={32} />
              <Heading level={3}>{sourceMaterial ? 'Update' : 'Create'} source material</Heading>
            </div>
          }
        />
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <MobileHeader onBack={handleBack}>{'id' in sourceMaterial ? 'Update' : 'Create'} source material</MobileHeader>
      </ResponsiveDesign.Mobile>
      <div className={Styles.content}>
        <Preview sourceMaterial={sourceMaterial} />
        <div className={Styles.formGroup}>
          <label htmlFor="sourceMaterialNameInput">
            Name <span>*</span>
          </label>
          <div className={Styles.nameLine}>
            <div className={Styles.emojiContainer}>
              <EmojiPicker
                emoji={sourceMaterial.emoji || ''}
                buttonClass={Styles.emojiButton}
                onSelect={(emoji) => setSourceMaterial((material) => ({ ...material, emoji: emoji?.native || null }))}
                size="lg"
              />
            </div>
            <div className={Styles.input}>
              <Input
                autoFocus
                id="sourceMaterialNameInput"
                name="name"
                onChange={({ target: { value } }) => {
                  setSourceMaterial((material) => ({ ...material, name: value }))
                  setNameError(undefined)
                }}
                placeholder="What do you want to call this source material?"
                size="lg"
                value={sourceMaterial?.name || ''}
                errorMessage={(showError && !sourceMaterial.name?.length && 'Required') || nameError}
                isValid={!(showError && !sourceMaterial.name?.length) && !nameError}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal.Footer blaze className={Styles.footer}>
        {
          [
            !isMobile && (
              <Modal.FooterContent slot="left" key="left">
                <Modal.FooterButton size="lg" variant="tertiary" onPress={handleBack}>
                  Back
                </Modal.FooterButton>
              </Modal.FooterContent>
            ),
            <Modal.FooterContent slot="right" key="right">
              <Modal.FooterButton size="lg" isDisabled={isCreating || isUpdating} onPress={handleSubmit}>
                {'id' in sourceMaterial ? 'Update' : 'Create'} Source Material
              </Modal.FooterButton>
            </Modal.FooterContent>,
          ].filter(Boolean) as ReactElement | [ReactElement, ReactElement]
        }
      </Modal.Footer>
    </Modal.Root>
  )
}
